/**
 * New Product Page JavaScript Module
 *
 * This module handles the functionality for the new product detail page (PDP) implementation.
 * It provides two main custom web components that work together to create a seamless
 * product browsing and purchasing experience.
 *
 * Main Components:
 * 1. ProductMediaGallery - Manages product image/video gallery with swiper functionality
 * 2. ProductInfo - Handles product options, artwork forms, cart functionality, and form submissions
 *
 * Key Features:
 * - Product media gallery with thumbnail navigation
 * - Dynamic variant selection and URL updates
 * - Artwork form integration with search capabilities
 * - Quantity controls with validation
 * - Color/style image mapping for product mockups
 * - Real-time add-to-cart button state management
 * - Shopping cart integration with offcanvas display
 * - Form validation and error handling
 * - Translation support for all user-facing strings
 *
 * Dependencies:
 * - Swiper.js for media gallery functionality
 * - Bootstrap for offcanvas cart display
 * - Shopify Cart API for add to cart functionality
 * - Custom artwork-form web component
 *
 * @version 2.0.0
 * @author The Custom Captain Development Team
 */

// Constants for configuration
const CONFIG = {
  API_ENDPOINTS: {
    CART_ADD: 'cart/add.js'
  },
  SELECTORS: {
    PRODUCT_FORM: '.js-product-form',
    QUANTITY_INPUT: '.js-quantity-input',
    QUANTITY_BTN_MINUS: '.js-quantity-btn-minus',
    QUANTITY_BTN_PLUS: '.js-quantity-btn-plus',
    ADD_TO_CART_BTN: '.new-product__add-to-cart-btn',
    FORM_SELECT: '.js-form-select',
    ERROR: '.js-error',
    ARTWORK_FORM: 'artwork-form',
    CART_BADGE: '.navbar-icons-cart .badge',
    OFFCANVAS_CART: '#offcanvas-cart',
    OFFCANVAS_CART_INNER: '#offcanvas-cart-inner'
  },
  DEBOUNCE_DELAY: 100,
  SWIPER_CONFIG: {
    THUMBNAILS: {
      slidesPerView: 3,
      slidesPerGroup: 3,
      spaceBetween: 9,
      freeMode: true,
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      breakpoints: {
        768: {
          direction: "vertical",
          spaceBetween: 13
        }
      }
    },
    MAIN: {
      slidesPerView: 1,
      spaceBetween: 12,
      thumbs: {
        slideThumbActiveClass: "swiper-slide-thumb-active"
      }
    }
  }
};

customElements.component = "product-media-gallery";

if (!customElements.get(customElements.component)) {
  /**
   * ProductMediaGallery Custom Element
   *
   * Manages the product media gallery including images and videos with swiper navigation.
   * Supports thumbnail navigation, video playback controls, and variant-based slide changes.
   */
  class NewProductMediaGallery extends HTMLElement {
    constructor() {
      super();
      this.initializeElements();
      this.initializeState();
    }

    /**
     * Initialize DOM element references
     * @private
     */
    initializeElements() {
      this.mainSlider = this.querySelector(".product-media-gallery__main-slider");
      this.thumbnailsSlider = this.querySelector(".product-media-gallery__thumbnails");
      this.navPrev = this.querySelector(".product-media-gallery-prev");
      this.navNext = this.querySelector(".product-media-gallery-next");
      this.videoPlayIcons = this.querySelectorAll(".js-play-icon");
      this.videoContainers = this.querySelectorAll(".js-video-container");
    }

    /**
     * Initialize component state
     * @private
     */
    initializeState() {
      this.activeMainSlider = null;
      this.activeThumbnailsSlider = null;
      this.swiperCheckInterval = null;
    }

    /**
     * Set up event listeners for gallery interactions
     * @private
     */
    setEventListeners() {
      document.addEventListener("product-option-change", this.onVariantChanged);
      this.videoPlayIcons?.forEach(icon => {
        icon.addEventListener("click", this.handleVideoPlay);
      });
    }

    /**
     * Navigate to slide based on variant selection
     * @param {object} variant - variant object
     */
    setActiveSlide = (variant) => {
      try {
        if (variant?.featured_media?.position && this.activeMainSlider) {
          this.activeMainSlider.slideTo(Number(variant.featured_media.position) - 1);
        }
      } catch (error) {
        console.error('Error parsing variant data:', error);
      }
    }

    /**
     * Initialize Swiper instances for main and thumbnail galleries
     * @private
     */
    initializeSliders() {
      if (!this.mainSlider) return;

      const mainOptions = {
        ...CONFIG.SWIPER_CONFIG.MAIN,
        on: {
          slideChange: () => this.handleVideoPause()
        }
      };

      // Thumbnails are only rendered when the product has more than one media item
      if (this.thumbnailsSlider) {
        this.activeThumbnailsSlider = new Swiper(this.thumbnailsSlider, {
          ...CONFIG.SWIPER_CONFIG.THUMBNAILS,
          navigation: this.buildNavigationConfig()
        });

        mainOptions.thumbs = {
          ...CONFIG.SWIPER_CONFIG.MAIN.thumbs,
          swiper: this.activeThumbnailsSlider
        };
      } else {
        delete mainOptions.thumbs;
      }

      this.activeMainSlider = new Swiper(this.mainSlider, mainOptions);
    }

    /**
     * Build navigation configuration for sliders
     * @private
     * @returns {Object} Navigation configuration object
     */
    buildNavigationConfig() {
      const navigation = {};
      if (this.navNext && this.navPrev) {
        navigation.nextEl = this.navNext;
        navigation.prevEl = this.navPrev;
      }
      return navigation;
    }

    /**
     * Clean up Swiper instances
     * @private
     */
    destroySwipers() {
      if (this.activeMainSlider) {
        this.activeMainSlider.destroy(true, true);
        this.activeMainSlider = null;
      }
      if (this.activeThumbnailsSlider) {
        this.activeThumbnailsSlider.destroy(true, true);
        this.activeThumbnailsSlider = null;
      }
    }

    /**
     * Pause all videos in the gallery
     * @private
     */
    handleVideoPause() {
      this.videoContainers?.forEach(container => {
        const videoElement = container.querySelector(".js-video");
        if (videoElement) videoElement.pause();
        container.classList.remove("is-active");
      });
    }

    /**
     * Handle video play button clicks
     * @private
     * @param {Event} event - Click event
     */
    handleVideoPlay = (event) => {
      const playIcon = event.target.closest(".js-play-icon");
      if (!playIcon) return;

      const videoContainer = playIcon.parentElement;
      const videoElement = videoContainer.querySelector(".js-video");

      if (videoElement) {
        videoContainer.classList.add("is-active");
        videoElement.play();
      }
    };

    /**
     * Handle variant change events
     * @private
     * @param {CustomEvent} event - Variant change event
     */
    onVariantChanged = (event) => {
      if (event.detail?.variant) {
        this.setActiveSlide(event.detail.variant);
      }
    };

    /**
     * Initialize gallery when component is connected to DOM
     */
    connectedCallback() {
      this.initializeElements();

      this.waitForSwiper(() => {
        this.initializeSliders();
        this.setEventListeners();
      });
    }

    /**
     * Wait for Swiper library to be available
     * @private
     * @param {Function} callback - Function to execute when Swiper is ready
     */
    waitForSwiper(callback) {
      if (typeof Swiper !== "undefined") {
        callback();
        return;
      }

      clearInterval(this.swiperCheckInterval);
      this.swiperCheckInterval = setInterval(() => {
        if (typeof Swiper === "undefined") return;

        clearInterval(this.swiperCheckInterval);
        this.swiperCheckInterval = null;

        // The element may have been removed while waiting (e.g. theme editor re-render)
        if (this.isConnected) callback();
      }, CONFIG.DEBOUNCE_DELAY);
    }

    /**
     * Clean up when component is disconnected from DOM
     */
    disconnectedCallback() {
      clearInterval(this.swiperCheckInterval);
      this.swiperCheckInterval = null;
      this.destroySwipers();
      document.removeEventListener("product-option-change", this.onVariantChanged);
      this.videoPlayIcons?.forEach(icon => {
        icon.removeEventListener("click", this.handleVideoPlay);
      });
    }
  }

  customElements.define(customElements.component, NewProductMediaGallery);
}

customElements.component = "product-info";

if (!customElements.get(customElements.component)) {
  /**
   * ProductInfo Custom Element
   *
   * Manages product information, variant selection, artwork forms, quantity controls,
   * and add-to-cart functionality. Handles form validation, state management, and
   * integration with the shopping cart.
   */
  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.initializeVariables();
      this.initializeArtworkState();
    }

    /**
     * Initialize component and set up all functionality
     */
    init() {
      this.initializeVariables();
      this.initializeEventListeners();
      this.syncOptionsWithVariantFromURL();
      this.updateAddToCartState();
      this.initializeQuantityControls();
      this.initializeArtworkIntegration();
      this.initializeColorStyleImageMappings();
      this.updateArtworkInputState();
    }

    /**
     * Initialize DOM element references and component variables
     * @private
     */
    initializeVariables() {
      this.form = this.querySelector(CONFIG.SELECTORS.PRODUCT_FORM);
      this.sectionId = this.dataset.sectionId;
      this.error = this.querySelector(CONFIG.SELECTORS.ERROR);

      this.quantityInput = this.querySelector(CONFIG.SELECTORS.QUANTITY_INPUT);
      this.quantityBtnMinus = this.querySelector(CONFIG.SELECTORS.QUANTITY_BTN_MINUS);
      this.quantityBtnPlus = this.querySelector(CONFIG.SELECTORS.QUANTITY_BTN_PLUS);
      this.addToCartBtn = this.form?.querySelector(CONFIG.SELECTORS.ADD_TO_CART_BTN);

      this.artworkForm = this.querySelector(CONFIG.SELECTORS.ARTWORK_FORM);
      this.optionFields = Array.from(this.form?.querySelectorAll(CONFIG.SELECTORS.FORM_SELECT));
      this.optionNames = this.extractOptionNames();

      this.translations = this.getTranslations();
    }

    /**
     * Extract option names from form fields
     * @private
     * @returns {Array} Array of option names
     */
    extractOptionNames() {
      return this.optionFields?.map(field =>
        field.getAttribute('name') || field.querySelector('input,select')?.getAttribute('name')
      );
    }

    /**
     * Get translation strings for the component
     * @private
     * @returns {Object} Translation object
     */
    getTranslations() {
      return window.productStrings || {
        sold_out: 'Sold out',
        choose_option: 'Choose ',
        add_to_cart: 'Add to cart',
        enter_order_or_email: 'Please enter order number or email',
        searching_for_artwork: 'Searching for artwork...',
        no_artwork_available: 'No artwork available',
        select_artwork: 'Select artwork',
        adding_to_cart: 'Adding...',
        loading_spinner_html: '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>',
        select_artwork_type: 'Select '
      };
    }

    /**
     * Initialize artwork state management
     * @private
     */
    initializeArtworkState() {
      this.artworkState = {
        isSearching: false,
        hasInput: false,
        artworkRequirements: {},
        allArtworkSelected: false,
        artworkFound: false,
        artworkCount: 0
      };
    }

    /**
     * Set up all event listeners
     * @private
     */
    initializeEventListeners() {
      this.form?.addEventListener('change', this.handleFormChange.bind(this));
      this.form?.addEventListener('submit', this.handleFormSubmit.bind(this));
      window.addEventListener('artwork:prefill', this.updateArtworkInputState.bind(this));
    }

    /**
     * Initialize artwork form integration
     * @private
     */
    initializeArtworkIntegration() {
      if (this.artworkForm) {
        this.artworkForm.addEventListener('artwork:statechange', this.handleArtworkStateChange.bind(this));
        this.updateArtworkInputState();
      }
    }

    /**
     * Update artwork input state based on option selection
     * @private
     */
    updateArtworkInputState() {
      if (!this.artworkForm) return;

      const artworkInput = this.artworkForm.querySelector('input[type="text"]');
      if (!artworkInput) return;

      const allOptionsSelected = this.areAllOptionsSelected();
      
      // Disable/enable artwork input based on option selection
      artworkInput.disabled = !allOptionsSelected && artworkInput.value === '';
      
      // Add visual feedback for disabled state
      if (!allOptionsSelected && artworkInput.value === '') {
        artworkInput.classList.add('disabled');
      } else {
        artworkInput.classList.remove('disabled');
      }
    }

    /**
     * Handle artwork state changes from artwork form component
     * @private
     * @param {CustomEvent} event - Artwork state change event
     */
    handleArtworkStateChange(event) {
      this.artworkState = event.detail;
      this.updateAddToCartState();
    }

    /**
     * Initialize color and style image mapping functionality
     * Migrated from legacy product.js and product-form-api.js
     * @private
     */
    initializeColorStyleImageMappings() {
      if (!this.form) return;
      this.setupColorImageMapping();
    }

    /**
     * Set up color to image mapping for front/back artwork
     * @private
     */
    setupColorImageMapping() {
      const colorsInputs = this.form.querySelectorAll('input[name="color"]') ||
                          this.form.querySelectorAll('input[name="colors"]');
      const frontPropertiesInput = this.form.querySelector('input[name="properties[select-front]"]');
      const backPropertiesInput = this.form.querySelector('input[name="properties[select-back]"]');

      if (this.hasColorImageMappingData(colorsInputs, frontPropertiesInput, backPropertiesInput)) {
        this.attachColorChangeListeners(colorsInputs, frontPropertiesInput, backPropertiesInput);
      }
    }

    /**
     * Check if color image mapping data is available
     * @private
     * @param {NodeList} colorsInputs - Color input elements
     * @param {Element} frontInput - Front properties input
     * @param {Element} backInput - Back properties input
     * @returns {boolean} Whether mapping data is available
     */
    hasColorImageMappingData(colorsInputs, frontInput, backInput) {
      return colorsInputs &&
             window.front_colors_images &&
             window.back_colors_images &&
             frontInput &&
             backInput;
    }

    /**
     * Attach color change listeners for image mapping
     * @private
     * @param {NodeList} colorsInputs - Color input elements
     * @param {Element} frontInput - Front properties input
     * @param {Element} backInput - Back properties input
     */
    attachColorChangeListeners(colorsInputs, frontInput, backInput) {
      colorsInputs.forEach((colorInput) => {
        colorInput.addEventListener("change", (e) => {
          const selectedColor = e.target.value;
          frontInput.value = window.front_colors_images[selectedColor];
          backInput.value = window.back_colors_images[selectedColor];
        });
      });
    }

    /**
     * Initialize quantity control functionality
     * @private
     */
    initializeQuantityControls() {
      if (this.hasQuantityControls()) {
        this.removeQuantityListeners();
        this.attachQuantityListeners();
        this.setQuantity(this.getQuantity());
      }
    }

    /**
     * Check if quantity controls are available
     * @private
     * @returns {boolean} Whether quantity controls exist
     */
    hasQuantityControls() {
      return this.quantityInput && this.quantityBtnMinus && this.quantityBtnPlus;
    }

    /**
     * Remove quantity control event listeners
     * @private
     */
    removeQuantityListeners() {
      if (this.quantityBtnMinus) {
        this.quantityBtnMinus.removeEventListener('click', this.handleQuantityMinus);
      }
      if (this.quantityBtnPlus) {
        this.quantityBtnPlus.removeEventListener('click', this.handleQuantityPlus);
      }
      if (this.quantityInput) {
        this.quantityInput.removeEventListener('input', this.handleQuantityInput);
      }
    }

    /**
     * Attach quantity control event listeners
     * @private
     */
    attachQuantityListeners() {
      // Bind event handlers to instance methods to enable proper removal
      this.handleQuantityMinus = () => {
        this.setQuantity(this.getQuantity() - 1);
      };
      
      this.handleQuantityPlus = () => {
        this.setQuantity(this.getQuantity() + 1);
      };
      
      this.handleQuantityInput = () => {
        this.setQuantity(this.getQuantity());
      };

      this.quantityBtnMinus.addEventListener('click', this.handleQuantityMinus);
      this.quantityBtnPlus.addEventListener('click', this.handleQuantityPlus);
      this.quantityInput.addEventListener('input', this.handleQuantityInput);
    }

    /**
     * Sync product options with variant from URL parameters
     * @private
     */
    syncOptionsWithVariantFromURL() {
      const variantId = this.getVariantIdFromURL();
      if (!variantId) return;

      const variant = this.findVariantById(variantId);
      if (!variant) return;

      this.selectVariantOptions(variant);
      this.updateArtworkInputState();
    }

    /**
     * Get variant ID from URL parameters
     * @private
     * @returns {string|null} Variant ID or null
     */
    getVariantIdFromURL() {
      const url = new URL(window.location.href);
      return url.searchParams.get('variant');
    }

    /**
     * Find variant by ID in available variants
     * @private
     * @param {string} variantId - Variant ID to find
     * @returns {Object|null} Variant object or null
     */
    findVariantById(variantId) {
      let variants = [];

      if (this.form.dataset.variants) {
        variants = JSON.parse(this.form.dataset.variants);
      } else if (window.product && window.product.variants) {
        variants = window.product.variants;
      }

      return variants.find(v => String(v.id) === String(variantId));
    }

    /**
     * Select options based on variant data
     * @private
     * @param {Object} variant - Variant object
     */
    selectVariantOptions(variant) {
      this.optionFields.forEach((field, idx) => {
        const value = variant.options[idx];
        this.selectFieldValue(field, value);
      });
    }

    /**
     * Select value in form field
     * @private
     * @param {Element} field - Form field element
     * @param {string} value - Value to select
     */
    selectFieldValue(field, value) {
      if (field.tagName === 'SELECT') {
        field.value = value;
      } else if (field.tagName === 'FIELDSET') {
        const input = field.querySelector(`input[value='${value}']`);
        if (input) input.checked = true;
      }
    }

    /**
     * Update add to cart button state based on current form state
     * @private
     */
    updateAddToCartState() {
      if (this.isProductSoldOut()) {
        this.setButtonState(true, this.translations.sold_out);
        return;
      }

      if (!this.areAllOptionsSelected()) {
        this.handleMissingOptions();
        return;
      }

      if (!this.isArtworkRequirementMet()) {
        this.handleArtworkRequirements();
        return;
      }

      this.setButtonState(false, this.translations.add_to_cart);
    }

    /**
     * Check if product is sold out
     * @private
     * @returns {boolean} Whether product is sold out
     */
    isProductSoldOut() {
      return this.addToCartBtn?.dataset.disabled === 'true';
    }

    /**
     * Check if all required options are selected
     * @private
     * @returns {boolean} Whether all options are selected
     */
    areAllOptionsSelected() {
      const selected = this.optionFields.map(field => {
        if (field.tagName === 'SELECT') {
          return field.value !== '';
        } else if (field.tagName === 'FIELDSET') {
          return !!field.querySelector('input:checked');
        }
        return false;
      });

      return !selected.includes(false);
    }

    /**
     * Handle missing option selections
     * @private
     */
    handleMissingOptions() {
      const firstUnselectedIdx = this.getFirstUnselectedOptionIndex();
      if (firstUnselectedIdx !== -1) {
        const optionName = this.optionNames[firstUnselectedIdx];
        const label = `${this.translations.choose_option}${optionName}`;
        this.setButtonState(true, label);
      }
    }

    /**
     * Get index of first unselected option
     * @private
     * @returns {number} Index of first unselected option or -1
     */
    getFirstUnselectedOptionIndex() {
      const selected = this.optionFields.map(field => {
        if (field.tagName === 'SELECT') {
          return field.value !== '';
        } else if (field.tagName === 'FIELDSET') {
          return !!field.querySelector('input:checked');
        }
        return false;
      });

      return selected.indexOf(false);
    }

    /**
     * Check if artwork requirements are met
     * @private
     * @returns {boolean} Whether artwork requirements are satisfied
     */
    isArtworkRequirementMet() {
      if (!this.artworkForm) return true;

      if (!this.artworkState.hasInput) return false;
      if (this.artworkState.isSearching) return false;
      if (!this.artworkState.artworkFound) return false;

      // All artwork must be selected for requirements to be met
      return this.artworkState.allArtworkSelected;
    }

    /**
     * Handle artwork-related button states
     * @private
     */
    handleArtworkRequirements() {
      if (!this.artworkState.hasInput) {
        this.setButtonState(true, this.translations.enter_order_or_email);
        return;
      }

      if (this.artworkState.isSearching) {
        this.setButtonState(true, this.translations.searching_for_artwork);
        return;
      }

      if (!this.artworkState.artworkFound) {
        this.setButtonState(true, this.translations.no_artwork_available);
        return;
      }

      // If artwork is found but not all artwork is selected, show missing artwork message
      if (this.artworkState.hasInput && this.artworkState.artworkFound && !this.artworkState.allArtworkSelected) {
        const missingArtworkText = this.getMissingArtworkText();
        this.setButtonState(true, missingArtworkText);
        return;
      }

      // If we reach here, all requirements should be met, but double-check
      if (!this.artworkState.allArtworkSelected) {
        const missingArtworkText = this.getMissingArtworkText();
        this.setButtonState(true, missingArtworkText);
      }
    }

      /**
   * Get text for missing artwork selection
   * @private
   * @returns {string} Missing artwork text
   */
  getMissingArtworkText() {
    const artworkRequirements = Object.entries(this.artworkState.artworkRequirements);
    const missingArtwork = artworkRequirements.find(([, selected]) => !selected);

    if (missingArtwork && missingArtwork[0]) {
      const artworkName = missingArtwork[0].replace('select-', '').replace(/-/g, ' ');
      const selectArtworkType = this.translations.select_artwork_type || 'Select ';
      return `${selectArtworkType}${artworkName}`;
    }

    return this.translations.select_artwork || 'Select artwork';
  }

    /**
     * Set button state (enabled/disabled) and text
     * @private
     * @param {boolean} disabled - Whether button should be disabled
     * @param {string} text - Button text
     */
    setButtonState(disabled, text) {
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = disabled;
        if (text) {
          this.addToCartBtn.textContent = text;
        }
      }
    }

    /**
     * Get current quantity value
     * @returns {number} Current quantity
     */
    getQuantity() {
      let value = Number(this.quantityInput?.value);
      const min = Number(this.quantityInput?.min) || 1;
      return isNaN(value) ? min : value;
    }

    /**
     * Set quantity value with validation
     * @param {number} value - Quantity value to set
     */
    setQuantity(value) {
      if (!this.quantityInput) return;

      const min = Number(this.quantityInput.min) || 1;
      const max = Number(this.quantityInput.max);

      if (isNaN(value) || value < min) value = min;
      if (max && value > max) value = max;

      this.quantityInput.value = value;
      this.updateQuantityButtons(value, min, max);
    }

    /**
     * Update quantity button states
     * @private
     * @param {number} value - Current quantity value
     * @param {number} min - Minimum allowed value
     * @param {number} max - Maximum allowed value
     */
    updateQuantityButtons(value, min, max) {
      if (this.quantityBtnMinus) {
        this.quantityBtnMinus.disabled = value <= min;
      }
      if (this.quantityBtnPlus) {
        this.quantityBtnPlus.disabled = max ? value >= max : false;
      }
    }

    /**
     * Set form disabled state (visually and functionally)
     * @private
     * @param {boolean} disabled
     */
    setFormDisabled(disabled) {
      if (!this.form) return;
      if (disabled) {
        this.form.classList.add('is-loading');
      } else {
        this.form.classList.remove('is-loading');
      }
      const controls = this.form.querySelectorAll('input[type="radio"], select');
      controls.forEach(el => el.disabled = disabled);
    }

    /**
     * Handle form change events
     * @private
     * @param {Event} event - Change event
     */
    handleFormChange(event) {
      this.setButtonState(true);

      event.preventDefault();
      if (this.shouldIgnoreFormChange(event)) return;

      const currentOptionValues = this.selectedOptionValues;

      if (this.variantChangeTimer) clearTimeout(this.variantChangeTimer);
      this.setFormDisabled(true);

      this.variantChangeTimer = setTimeout(() => {
        // Use the most recent option values (from the last change before the timeout)
        this.updateProductMarkup(currentOptionValues)
          .finally(() => {
            this.setFormDisabled(false);
          });
      }, 500);
    }

    /**
     * Check if form change should be ignored
     * @private
     * @param {Event} event - Change event
     * @returns {boolean} Whether to ignore the change
     */
    shouldIgnoreFormChange(event) {
      // Ignore quantity input changes (handled separately)
      if (event.target.matches('input[type="number"]')) {
        return true;
      }
      
      // Allow artwork form changes to trigger add-to-cart state updates
      // The artwork form handles its own state, but we need to update the button state
      if (event.target.closest(CONFIG.SELECTORS.ARTWORK_FORM)) {
        // Update add-to-cart state when artwork form changes
        this.updateAddToCartState();
        return true; // Still ignore the main form change handler
      }
      
      return false;
    }

    /**
     * Fetch updated product information from server
     * @private
     * @param {URL} url - URL to fetch from
     * @returns {Promise<Document>} Parsed HTML document
     */
    async fetchProductInfoHtml(url) {
      try {
        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        const parser = new DOMParser();
        return parser.parseFromString(text, 'text/html');
      } catch (error) {
        console.error('Error fetching product info:', error);
        throw error;
      }
    }

    /**
     * Update product markup based on selected options
     * @private
     * @param {Array} optionValues - Selected option values
     */
    async updateProductMarkup(optionValues) {
      const prevSelections = this.saveCurrentSelections();
      const artworkData = this.saveArtworkData();

      try {

        const url = this.buildUpdateURL(optionValues);
        const doc = await this.fetchProductInfoHtml(url);

        this.updateMasterVariant(doc);
        this.setupStyleSleeveMapping(doc);
        this.updateProductElements(doc);
        this.restoreSelections(prevSelections);

        this.updateAddToCartState();
        this.updateArtworkInputState();
        this.dispatchVariantChangeEvent(doc);
      } catch (error) {
        console.error('Error updating product markup:', error);
      }
    }

    /**
     * Update master variant input value
     * @private
     * @param {Document} doc - New document with updated markup
     */
    updateMasterVariant(doc) {
      const masterVariant = JSON.parse(doc.querySelector('.js-product-info').dataset.selectedVariant);
      const masterVariantInput = this.form.querySelector('input[name="id"]');
      if (masterVariantInput) {
        masterVariantInput.value = masterVariant.id;
      }
    }

    /**
     * Set up style to sleeve image mapping
     * @private
     */
    setupStyleSleeveMapping(doc) {
      const selector = 'input[name="properties[select-sleeve]"]';
      const propertyInput = this.form.querySelector(selector);
      const updatedValueElement = doc.querySelector(selector);
      if (propertyInput && updatedValueElement) {
        propertyInput.value = updatedValueElement.value;
      }
    }

    /**
     * Build URL for product update request
     * @private
     * @param {Array} optionValues - Selected option values
     * @returns {URL} Update URL
     */
    buildUpdateURL(optionValues) {
      const url = new URL(window.location.href);
      url.searchParams.set('option_values', optionValues.join(','));
      url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    /**
     * Update product elements with new markup
     * @private
     * @param {Document} doc - New document with updated markup
     */
    updateProductElements(doc) {
      this.updateElement(doc, '.new-product__prices');
      this.updateElement(doc, '.new-product__add-to-cart-btn');
      this.updateElement(doc, '#product-options');
      this.updateElement(doc, 'product-media-gallery');
    }

    /**
     * Update a specific element with new content
     * @private
     * @param {Document} doc - Source document
     * @param {string} selector - Element selector
     */
    updateElement(doc, selector) {
      const newElement = doc.querySelector(selector);
      const currentElement = this.querySelector(selector);

      if (newElement && currentElement) {
        currentElement.innerHTML = newElement.innerHTML;
      }
    }

    /**
     * Save current form selections
     * @private
     * @returns {Object} Current selections state
     */
    saveCurrentSelections() {
      const selections = {
        options: {},
        quantity: this.quantityInput ? Number(this.quantityInput.value) : 1
      };

      this.optionFields.forEach(field => {
        const fieldData = this.extractFieldData(field);
        if (fieldData.name && fieldData.value) {
          selections.options[fieldData.name] = fieldData.value;
        }
      });

      return selections;
    }

    /**
     * Extract data from form field
     * @private
     * @param {Element} field - Form field element
     * @returns {Object} Field data
     */
    extractFieldData(field) {
      switch (field.tagName) {
        case 'SELECT':
          return { name: field.name, value: field.value };
        case 'FIELDSET':
          const checked = field.querySelector('input:checked');
          return checked ? { name: checked.name, value: checked.value } : {};
        default:
          return {};
      }
    }

    /**
     * Save artwork form data
     * @private
     * @returns {Object} Artwork data
     */
    saveArtworkData() {
      if (!this.artworkForm) return null;

      return {
        inputValue: this.artworkForm.input?.value,
        state: this.artworkForm.getArtworkState()
      };
    }

    /**
     * Restore form selections
     * @private
     * @param {Object} selections - Previously saved selections
     */
    restoreSelections(selections) {
      this.optionFields.forEach(field => {
        this.restoreFieldSelection(field, selections.options);
      });

      if (this.quantityInput && !isNaN(selections.quantity)) {
        this.setQuantity(selections.quantity);
      }

      // Update artwork input state after restoring selections
      this.updateArtworkInputState();
    }

    /**
     * Restore selection for a specific field
     * @private
     * @param {Element} field - Form field element
     * @param {Object} savedOptions - Saved option values
     */
    restoreFieldSelection(field, savedOptions) {
      switch (field.tagName) {
        case 'SELECT':
          const selectName = field.getAttribute('name');
          if (selectName && savedOptions[selectName]) {
            field.value = savedOptions[selectName];
          }
          break;
        case 'FIELDSET':
          const fieldsetName = field.querySelector('input')?.name;
          if (fieldsetName && savedOptions[fieldsetName]) {
            const input = field.querySelector(`input[value='${savedOptions[fieldsetName]}']`);
            if (input) input.checked = true;
          }
          break;
      }
    }

    /**
     * Dispatch variant change event
     * @private
     * @param {Document} doc - Updated document
     */
    dispatchVariantChangeEvent(doc) {
      const productInfo = doc.querySelector('.js-product-info');
      if (productInfo) {
        document.dispatchEvent(new CustomEvent('product-option-change', {
          detail: {
            bubbles: true,
            variant: JSON.parse(productInfo.dataset.selectedVariant)
          }
        }));
      }
    }

    /**
     * Handle form submission for add to cart
     * @private
     * @param {Event} event - Submit event
     */
    handleFormSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.processAddToCart();
    }

    /**
     * Process add to cart request
     * @private
     */
    async processAddToCart() {
      const originalText = this.addToCartBtn.textContent;
      this.setLoadingState();

      try {
        const formData = new FormData(this.form);
        const response = await this.submitToCart(formData);
        const data = await response.json();

        await this.handleSuccessfulAddToCart();
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError();
      } finally {
        this.resetButtonState(originalText);
      }
    }

    /**
     * Set button to loading state
     * @private
     */
    setLoadingState() {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.innerHTML = `${this.translations.loading_spinner_html}${this.translations.adding_to_cart}`;
    }

    /**
     * Submit form data to cart API
     * @private
     * @param {FormData} formData - Form data to submit
     * @returns {Promise<Response>} Fetch response
     */
    async submitToCart(formData) {
      const params = new URLSearchParams(formData);
      return fetch(window.Shopify.routes.root + CONFIG.API_ENDPOINTS.CART_ADD, {
        method: 'POST',
        body: params
      });
    }

    /**
     * Handle successful add to cart
     * @private
     */
    async handleSuccessfulAddToCart() {
      await this.updateCartDisplay();
      this.showCartOffcanvas();
      this.initializeCartFunctionality();
      this.saveArtworkOrderId();
    }

    /**
     * Update cart display elements
     * @private
     */
    async updateCartDisplay() {
      try {
        const response = await fetch(window.location.href);
        const text = await response.text();
        const parser = new DOMParser();
        const newDocument = parser.parseFromString(text, 'text/html');

        this.updateCartBadges(newDocument);
        this.updateCartOffcanvas(newDocument);
      } catch (error) {
        console.error('Error updating cart display:', error);
      }
    }

    /**
     * Update cart badge elements
     * @private
     * @param {Document} newDocument - Updated document
     */
    updateCartBadges(newDocument) {
      const newBadge = newDocument.querySelector(CONFIG.SELECTORS.CART_BADGE);
      if (newBadge) {
        document.querySelectorAll(CONFIG.SELECTORS.CART_BADGE).forEach((el) => {
          const updatedBadge = newDocument.querySelector(`#${el.id}`);
          if (updatedBadge) el.replaceWith(updatedBadge);
        });
      }
    }

    /**
     * Update cart offcanvas content
     * @private
     * @param {Document} newDocument - Updated document
     */
    updateCartOffcanvas(newDocument) {
      const newOffcanvas = newDocument.querySelector(CONFIG.SELECTORS.OFFCANVAS_CART_INNER);
      if (newOffcanvas) {
        document.querySelector(CONFIG.SELECTORS.OFFCANVAS_CART_INNER)?.replaceWith(newOffcanvas);
      }
    }

    /**
     * Show cart offcanvas
     * @private
     */
    showCartOffcanvas() {
      const offcanvas = new bootstrap.Offcanvas(
        document.querySelector(CONFIG.SELECTORS.OFFCANVAS_CART)
      );
      offcanvas.show();
    }

    /**
     * Initialize cart functionality
     * @private
     */
    initializeCartFunctionality() {
      if (window.initCartQty) {
        window.initCartQty();
      }
    }

    /**
     * Save artwork order ID to local storage
     * @private
     */
    saveArtworkOrderId() {
      const artworkInput = this.artworkForm?.input;
      if (artworkInput?.value) {
        localStorage.setItem('artwork-order-id', artworkInput.value);
      }
    }

    /**
     * Reset button to original state
     * @private
     * @param {string} originalText - Original button text
     */
    resetButtonState(originalText) {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.innerHTML = originalText;
    }

    /**
     * Show error state
     * @private
     */
    showError() {
      this.error?.classList.add('is-active');
    }

    /**
     * Initialize component when connected to DOM
     */
    connectedCallback() {
      this.init();
    }

    /**
     * Clean up when component is disconnected from DOM
     */
    disconnectedCallback() {
      this.form?.removeEventListener('change', this.handleFormChange.bind(this));
      this.form?.removeEventListener('submit', this.handleFormSubmit.bind(this));
      window.removeEventListener('artwork:prefill', this.updateArtworkInputState.bind(this));

      if (this.artworkForm) {
        this.artworkForm.removeEventListener('artwork:statechange', this.handleArtworkStateChange.bind(this));
      }

      // Clean up quantity listeners
      this.removeQuantityListeners();
      // Clean up variant change timer
      if (this.variantChangeTimer) {
        clearTimeout(this.variantChangeTimer);
        this.variantChangeTimer = null;
      }
    }

    /**
     * Get selected option values
     * @returns {Array} Array of selected option value IDs
     */
    get selectedOptionValues() {
      return Array.from(this.form.querySelectorAll('select option:checked, fieldset input:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
    }
  }

  customElements.define(customElements.component, ProductInfo);
}